import { ref, computed, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { actionsService } from '@/services/actionsService.js'
import { authorizedUsersService } from '@/services/adminService.js'
import { ACTION_TYPES } from '@/constants/actions.js'
import { useCurrentUser } from '@/composables/auth/useCurrentUser.js'

/**
 * Composable for ActionAssignmentModal form logic.
 * Handles form state, validation, type selection, employee assignment, and date handling.
 *
 * @param {import('vue').Ref<Object>} props - Component props (open, prospect, defaultAssignee)
 * @param {Function} emit - Component emit function
 */
export function useActionAssignmentForm(props, emit) {
  // State
  const actionTypes = ref([])
  const selectedActionType = ref(null)
  const employees = ref([])
  const selectedEmployee = ref(null)
  const employeeQuery = ref('')
  const employeeDropdownOpen = ref(false)
  const selectedPL = ref(null)
  const plQuery = ref('')
  const plDropdownOpen = ref(false)
  const dueDate = ref('')
  const description = ref('')
  const isSubmitting = ref(false)
  const errorMessage = ref('')
  const successMessage = ref('')

  const { getApiUserInfo } = useCurrentUser()

  // Filtered employees based on search query
  const filteredEmployees = computed(() => {
    if (employeeQuery.value === '') {
      return employees.value
    }
    const query = employeeQuery.value.toLowerCase()
    return employees.value.filter(
      employee =>
        employee.name.toLowerCase().includes(query) ||
        (employee.role && employee.role.toLowerCase().includes(query))
    )
  })

  // Check if selected action type is deal_no_deal
  const isDealNoDeal = computed(() => {
    return selectedActionType.value?.code === ACTION_TYPES.DEAL_NO_DEAL
  })

  // Filtered PL employees based on search query
  const filteredPLEmployees = computed(() => {
    if (plQuery.value === '') {
      return employees.value
    }
    const query = plQuery.value.toLowerCase()
    return employees.value.filter(
      employee =>
        employee.name.toLowerCase().includes(query) ||
        (employee.role && employee.role.toLowerCase().includes(query))
    )
  })

  // Computed
  const canSubmit = computed(() => {
    return (
      selectedActionType.value &&
      selectedEmployee.value &&
      dueDate.value &&
      isValidDate(dueDate.value)
    )
  })

  // Display date in dd/mm/yyyy format
  const displayDate = computed(() => {
    if (!dueDate.value) return ''
    // dueDate is stored as YYYY-MM-DD
    const [year, month, day] = dueDate.value.split('-')
    if (!year || !month || !day) return dueDate.value
    return `${day}/${month}/${year}`
  })

  // Handle date input in dd/mm/yyyy format
  function handleDateInput(event) {
    const value = event.target.value
    // Allow typing with auto-formatting
    const digits = value.replace(/\D/g, '')
    let formatted = ''
    if (digits.length > 0) formatted += digits.substring(0, 2)
    if (digits.length > 2) formatted += '/' + digits.substring(2, 4)
    if (digits.length > 4) formatted += '/' + digits.substring(4, 8)
    event.target.value = formatted

    // Convert to YYYY-MM-DD for storage
    if (digits.length === 8) {
      const day = digits.substring(0, 2)
      const month = digits.substring(2, 4)
      const year = digits.substring(4, 8)
      dueDate.value = `${year}-${month}-${day}`
    } else {
      dueDate.value = ''
    }
  }

  // Validate date string (YYYY-MM-DD)
  function isValidDate(dateString) {
    if (!dateString) return false
    const date = new Date(dateString)
    return date instanceof Date && !isNaN(date)
  }

  // Format date to YYYY-MM-DD
  const formatDate = date => {
    return date.toISOString().split('T')[0]
  }

  // Get default due date (1 week from now)
  const getDefaultDueDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 7)
    return formatDate(date)
  }

  // Cached action types via TanStack Query (rarely change)
  const { data: cachedActionTypes } = useQuery({
    queryKey: ['action-types'],
    queryFn: () => actionsService.getTypes(),
    staleTime: 1000 * 60 * 30 // 30 minutes
  })

  // Cached authorized users via TanStack Query (rarely change)
  const { data: cachedEmployees } = useQuery({
    queryKey: ['authorized-users-active'],
    queryFn: () => authorizedUsersService.getActiveWithEmployee(),
    staleTime: 1000 * 60 * 10 // 10 minutes
  })

  // Sync cached data into local refs
  watch(
    cachedActionTypes,
    types => {
      if (types) actionTypes.value = types
    },
    { immediate: true }
  )

  watch(
    cachedEmployees,
    emps => {
      if (emps) employees.value = emps
    },
    { immediate: true }
  )

  // Apply form defaults from cached data when modal opens
  function applyDefaults() {
    // Auto-select first action type
    if (actionTypes.value.length > 0 && !selectedActionType.value) {
      selectedActionType.value = actionTypes.value[0]
    }

    // Default assignee: match by submitted_by_email (the commerciant)
    if (props.prospect?.submitted_by_email && !selectedEmployee.value) {
      const commerciant = employees.value.find(
        emp => emp.email?.toLowerCase() === props.prospect.submitted_by_email.toLowerCase()
      )
      if (commerciant) {
        selectedEmployee.value = commerciant
      }
    }

    // Pre-populate PL if deal_no_deal is already selected
    if (isDealNoDeal.value && props.prospect?.reviewed_by_name && !selectedPL.value) {
      const plEmployee = employees.value.find(
        emp => emp.name.toLowerCase() === props.prospect.reviewed_by_name.toLowerCase()
      )
      if (plEmployee) {
        selectedPL.value = plEmployee
      }
    }
  }

  // Create action
  const createAction = async () => {
    if (!canSubmit.value) return

    isSubmitting.value = true
    errorMessage.value = ''
    successMessage.value = ''

    try {
      const currentUser = getApiUserInfo()

      // Include all sub-actions automatically
      const allSubActionItems =
        selectedActionType.value?.items?.map(item => ({
          action_item_type_id: item.id
        })) || []

      const actionData = {
        prospect_id: props.prospect.id,
        action_type_id: selectedActionType.value.id,
        assigned_to_name: selectedEmployee.value.name,
        assigned_by_name: currentUser.name,
        assigned_by_email: currentUser.email,
        due_date: dueDate.value,
        description: description.value.trim() || null,
        items: allSubActionItems
      }

      // Only include employee_id if available (some authorized users have no employee match)
      if (selectedEmployee.value.simplicate_id) {
        actionData.assigned_to_employee_id = selectedEmployee.value.simplicate_id
      }

      // Add PL as secondary assignee for deal_no_deal
      if (isDealNoDeal.value && selectedPL.value) {
        actionData.secondary_assigned_to_name = selectedPL.value.name
        if (selectedPL.value.simplicate_id) {
          actionData.secondary_assigned_to_employee_id = selectedPL.value.simplicate_id
        }
      }

      const result = await actionsService.create(actionData)

      if (result.success) {
        successMessage.value = 'Actie succesvol aangemaakt!'
        emit('created', result.data)

        // Close modal after short delay
        setTimeout(() => {
          emit('close')
        }, 1500)
      } else {
        errorMessage.value = result.error || 'Fout bij aanmaken van actie'
      }
    } catch (error) {
      console.error('Error creating action:', error)
      errorMessage.value = error.response?.data?.error || 'Fout bij aanmaken van actie'
    } finally {
      isSubmitting.value = false
    }
  }

  // Reset form when modal opens
  const resetForm = () => {
    selectedActionType.value = null
    selectedEmployee.value = null
    employeeQuery.value = ''
    selectedPL.value = null
    plQuery.value = ''
    description.value = ''
    errorMessage.value = ''
    successMessage.value = ''

    // Set default due date (1 week from now)
    dueDate.value = getDefaultDueDate()
  }

  // Pre-populate PL when deal_no_deal is selected
  watch(selectedActionType, newType => {
    if (
      newType?.code === ACTION_TYPES.DEAL_NO_DEAL &&
      props.prospect?.reviewed_by_name &&
      employees.value.length > 0
    ) {
      const plEmployee = employees.value.find(
        emp => emp.name.toLowerCase() === props.prospect.reviewed_by_name.toLowerCase()
      )
      if (plEmployee) {
        selectedPL.value = plEmployee
      }
    } else {
      selectedPL.value = null
      plQuery.value = ''
    }
  })

  // Watch for modal open — data comes from TanStack Query cache, no API calls needed
  watch(
    () => props.open,
    newVal => {
      if (newVal) {
        resetForm()
        applyDefaults()
      }
    }
  )

  return {
    // State
    actionTypes,
    selectedActionType,
    employees,
    selectedEmployee,
    employeeQuery,
    employeeDropdownOpen,
    selectedPL,
    plQuery,
    plDropdownOpen,
    dueDate,
    description,
    isSubmitting,
    errorMessage,
    successMessage,

    // Computed
    filteredEmployees,
    isDealNoDeal,
    filteredPLEmployees,
    canSubmit,
    displayDate,

    // Methods
    handleDateInput,
    createAction
  }
}
